<template>
	<v-dialog
		v-model="showDialog"
		max-width="960px"
		height="90%"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<v-card :tile="$vuetify.breakpoint.xs" :height="$vuetify.breakpoint.xs ? '' : '90vh'">
			<v-toolbar dark color="primary" dense flat>
				<v-toolbar-title style="color: white">ホールディングポイント結果報告</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon dark @click="showDialog = false" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-sheet class="pa-2" elevation="0">
				<v-row justify="end" no-gutters>
					<UploadFileDialog @uploadFiles="uploadFiles" />

					<CheckResultDialog
						v-if="
							holding_point.status == 'holding' &&
							permittedQuantity() < holding_point.order_individual_info.quantity
						"
						@accept="accept"
						:checkProcessName="checkProcessName()"
						:quantity="holding_point.order_individual_info.quantity - permittedQuantity()"
					/>
					<v-btn
						v-else-if="holding_point.status == 'checked'"
						class="mx-4"
						color="secondary"
						disabled
					>
						<v-icon class="mr-1">mdi-check-circle-outline</v-icon>
						許可済み</v-btn
					>
				</v-row>

				<table width="100%" class="mt-4 show-dialog-table">
					<tbody>
						<ShowDetailRecord
							:columnName="'製造番号'"
							:value="
								holding_point.order_individual_info
									? common.productNumberByOrderIndividualInfo(holding_point.order_individual_info)
									: ''
							"
						/>
						<ShowDetailRecord
							:columnName="'製品名'"
							:value="
								holding_point.order_individual_info &&
								holding_point.order_individual_info.ordered_product
									? common.productNameWithSubName(
											holding_point.order_individual_info.ordered_product
									  )
									: ''
							"
						/>
						<ShowDetailRecord
							:columnName="'数量'"
							:value="
								holding_point.order_individual_info
									? holding_point.order_individual_info.quantity
									: ''
							"
						/>
					</tbody>
				</table>
			</v-sheet>
			<v-sheet class="pa-2" elevation="0">
				<div>
					<table width="100%" class="show-dialog-table mt-1">
						<tbody>
							<tr>
								<th width="30%" class="font-weight-bold">工程名</th>
								<td width="70%">
									{{
										holding_point.check_process
											? holding_point.check_process.check_process_type.name
											: ""
									}}
								</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">状態</th>
								<td width="70%">{{ holding_point.status_i18n }}</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">許可済数量</th>
								<td width="70%">{{ permittedQuantity() }}</td>
							</tr>
							<tr>
								<th width="30%" class="font-weight-bold">添付資料</th>
								<td width="70%">
									<div
										v-for="file in holding_point.return_all_attached_file_infos"
										:key="`file-${file.id}`"
									>
										<a v-if="!file.destroy_flag" class="mt-2" :href="file.url" target="_blank">{{
											file.name
										}}</a
										><TemplateDeleteDialog
											v-if="!file.destroy_flag"
											onlyIcon
											:url="`/erp/v1/holding_points/files/${file.id}`"
											@success-delete="reloadFiles"
										>
											<template v-slot:title>ファイルの削除</template>
											<template v-slot:text> ファイルを削除してもよろしいですか？ </template>
										</TemplateDeleteDialog>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</v-sheet>

			<v-sheet class="pa-2" elevation="0">
				<div class="table-container">
					<table class="mt-2" v-if="holding_point.check_process">
						<thead>
							<tr>
								<th rowspan="2">項目</th>
								<th rowspan="2">方法</th>
								<th rowspan="2">基準</th>
								<template v-for="unit in holding_point.check_process.check_units">
									<th class="result-th" :key="`date-${unit.id}`">
										施工日：{{ common.formatDate(unit.check_date) }}
									</th>
									<th class="result-th" rowspan="2" :key="`user-${unit.id}`">確認者</th>
								</template>
							</tr>
							<tr>
								<th
									v-for="unit in holding_point.check_process.check_units"
									class="result-th"
									rowspan="2"
									:key="unit.id"
								>
									個数：{{ unit.quantity }}
								</th>
							</tr>
						</thead>
						<tbody v-if="holding_point && holding_point.check_process">
							<tr
								v-for="item in holding_point.check_process.check_process_type.check_items"
								:key="item.id"
							>
								<td>
									{{ item.name }}
								</td>
								<td>
									{{ item.check_method }}
								</td>
								<td>
									{{ item.standard }}
								</td>
								<template v-for="unit in holding_point.check_process.check_units">
									<td :key="`value-${unit.id}`">{{ displayValue(unit, item) }}</td>
									<td :key="`user-id-${unit.id}`">{{ unit.user.full_name }}</td>
								</template>
							</tr>
						</tbody>
					</table>
				</div>
			</v-sheet>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios"
import errorHandling from "../../../helper/errorHandling"
import authenticate from "../../../helper/authenticate"
import common from "../../../helper/common"
import ShowDetailRecord from "../../shared/ShowDetailRecord.vue"
import EditableCell from "../../shared/EditableCell.vue"

import TemplateDeleteDialog from "../../shared/TemplateDeleteDialog.vue"
import UploadFileDialog from "./UploadFileDialog.vue"
import CheckResultDialog from "./CheckResultDialog.vue"

export default {
	props: {
		dialog: {
			type: Boolean,
			required: false,
			default: false,
		},
		id: {
			type: null,
			required: false,
		},
		holdingPoint: {
			type: null,
			required: false,
		},
		authority_flag: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	components: {
		ShowDetailRecord,
		EditableCell,
		UploadFileDialog,
		CheckResultDialog,
		TemplateDeleteDialog,
	},
	data() {
		return {
			tab: 0,
			holding_point: {},
			common,
		}
	},
	methods: {
		openDialog() {
			this.showDialog = true
		},
		fetchHoldingPoints() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/holding_points/${this.holdingPoint.id}`
			axios
				.get(url, { headers })
				.then((res) => {
					this.holding_point = res.data.holding_point
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},

		displayValue(unit, item) {
			const value = unit.check_values.find((v) => v.check_item_id == item.id)
			return common.value(item, value?.value)
		},

		value(unit, item) {
			const value = unit.check_values.find((v) => v.check_item_id == item.id)
			if (typeof item == "boolean") {
				return item
			} else {
				return value?.value
			}
		},

		accept(quantity) {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/holding_points/${this.holding_point.id}/product_permissions`
			const params = { quantity: quantity, holding_point_id: this.holding_point.id }
			axios
				.post(url, params, { headers })
				.then((res) => {
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: `検査結果を許可しました`,
					})
					this.holding_point.status = res.data.holding_point.status
					this.holding_point.status_i18n = res.data.holding_point.status_i18n
					this.holding_point.product_permissions.push(res.data.product_permission)

					this.$emit("update", this.holding_point)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},

		checkProcessName() {
			if (!this.holding_point.check_process) return ""
			return `${this.holding_point.check_process.check_process_type.name}`
		},

		fileUpload(file, id) {
			const headers = authenticate.setHeader()
			headers["content-type"] = "multipart/form-data"
			const formData = new FormData()
			formData.append("holding_point_id", id)
			formData.append("file", file)
			const url = `/erp/v1/holding_points/files`
			axios
				.post(url, formData, { headers })
				.then((res) => {
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: `ファイルをアップロードしました`,
					})
					this.holding_point.return_all_attached_file_infos.push(res.data.attached_file)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},

		uploadFiles(files) {
			const holding_point_id = this.holding_point.id
			files.forEach((file) => {
				this.fileUpload(file, holding_point_id)
			})
		},

		reloadFiles(data) {
			this.holding_point.return_all_attached_file_infos =
				this.holding_point.return_all_attached_file_infos.filter((file) => file.id != data.file.id)
		},

		permittedQuantity() {
			if (!this.holding_point.product_permissions) return 0
			const total = this.holding_point.product_permissions.reduce((sum, permission) => {
				return sum + permission.permitted_quantity
			}, 0)
			return total
		},
	},
	computed: {
		showDialog: {
			get() {
				return this.dialog
			},
			set(newVal) {
				return this.$emit("update:dialog", newVal)
			},
		},
	},
	watch: {
		holdingPoint(newVal, oldVal) {
			if (newVal == oldVal) return
			this.fetchHoldingPoints()
		},
		deep: true,
	},
}
</script>
<style lang="scss" scoped>
.table-container {
	table {
		th {
			font-weight: bold;
			width: 200px;
			min-width: 200px;
			padding: 4px 8px;
			// background-color: lightgrey;
			background-color: whitesmoke;
			border-bottom: 1px solid white;
			border-right: 1px solid white;
		}
		th.result-th {
			background-color: whitesmoke;
		}
		td {
			width: 200px;
			min-width: 200px;
			padding: 4px;
			border: 1px solid whitesmoke;
			white-space: normal;
			p {
				white-space: pre-wrap;
			}
			p.no-wrap {
				white-space: nowrap;
			}
		}
	}
}
</style>
